<template>
  <div class="subheader">
    <!-- Period change : show all projects annuel associated to the projetAnnuel -->
    <SubHeaderItem v-show="projetAnnuel.anneeScolaireDebut" class="choose-period">
      <b-dropdown aria-role="list" @change="(index) => selectPeriod(index)">
        <template #trigger>
          <button class="navbar-item" type="button">
            <span v-if="projetAnnuel.typeDossier === 'DOSSIER_INSCRIPTION'">{{
              $t("type-dossier-shorter.DOSSIER_INSCRIPTION")
            }}</span>
            <span v-else>{{
              getPeriodLabel(projetAnnuel.anneeScolaireDebut, projetAnnuel.anneeScolaireFin)
            }}</span>
            <b-icon icon="angle-down"></b-icon>
          </button>
        </template>
        <div v-for="(period, index) in periods" :key="period.projetAnnuelId">
          <b-dropdown-item aria-role="listitem" :value="index">
            <span v-if="period.typeDossier === 'DOSSIER_INSCRIPTION'">{{
              $t("type-dossier-shorter.DOSSIER_INSCRIPTION")
            }}</span>
            <span v-else>{{
              getPeriodLabel(period.anneeScolaireDebut, period.anneeScolaireFin)
            }}</span>
          </b-dropdown-item>
        </div>
      </b-dropdown>
    </SubHeaderItem>

    <SubHeaderItem
      class="inscription-title"
      :class="{ 'only-in-inscription': periods.length == 1 }"
    >
      <span v-if="projetAnnuel.typeDossier == 'DOSSIER_INSCRIPTION'">
        {{ $t("projetDetails.inscription-title") }}
      </span>
      <span v-else-if="projetAnnuel.typeDossier == 'DOSSIER_LABELLISATION'">
        {{ $t("projetDetails.labellisation-title") }}
      </span>
      <span
        v-else-if="isPorteurProjet && projetAnnuel.typeDossier == 'DOSSIER_RENOUVELLEMENT_LIGHT'"
      >
        {{ $t("projetDetails.renouvellement-leger-title-porteur") }}
      </span>
      <span v-else-if="isPorteurProjet">
        {{ $t("projetDetails.renouvellement-lourd-title-porteur") }}
      </span>
      <span v-else-if="projetAnnuel.typeDossier == 'DOSSIER_RENOUVELLEMENT_LIGHT'">
        {{ $t("projetDetails.renouvellement-leger-title") }}
      </span>
      <span v-else>
        {{ $t("projetDetails.renouvellement-lourd-title") }}
      </span>
    </SubHeaderItem>

    <div class="end-sub-headers-container">
      <SubHeaderItem v-if="isPorteurProjet" class="date-depot" :icon="'calendar'">
        <!-- Inscription date -->
        <div v-if="projetAnnuel.typeDossier == 'DOSSIER_INSCRIPTION'">
          <div class="item-text">{{ $t("projetDetails.dateInscription") }}</div>
          <div class="item-text bold">
            {{
              $t("projetDetails.dateDepotRange", [
                formatDate(parametrage.debutInscription),
                formatDate(parametrage.finInscription),
              ])
            }}
          </div>
        </div>
        <div v-else>
          <!-- Labellisation date -->
          <div class="item-text">{{ $t("projetDetails.dateLabellisation") }}</div>
          <div class="item-text bold">
            {{
              $t("projetDetails.dateDepotRange", [
                formatDate(parametrage.debutLabellisation),
                formatDate(parametrage.finLabellisation),
              ])
            }}
          </div>
        </div>
      </SubHeaderItem>

      <!-- State change : to pause or archive a project -->
      <SubHeaderItem
        v-show="projetAnnuel.etat && projetAnnuel.typeDossier != 'DOSSIER_INSCRIPTION'"
        id="project-state"
        class="change-etat"
      >
        <b-dropdown
          v-if="canChangeEtat"
          v-model="selectedEtat"
          aria-role="list"
          :disabled="isObservateurNational"
          position="is-bottom-left"
          @change="changeEtat"
        >
          <template #trigger>
            <button class="navbar-item" type="button">
              <b-icon
                v-if="projetAnnuel.etat === 'ACTIF'"
                icon="play-circle"
                type="is-success"
              ></b-icon>
              <b-icon
                v-else-if="projetAnnuel.etat === 'EN_PAUSE'"
                icon="pause-circle"
                type="is-warning"
              ></b-icon>
              <b-icon v-else icon="stop-circle" type="is-danger"></b-icon>
              <span class="item-text bold">{{ $t(projetAnnuel.etat) }}</span>
              <b-icon icon="angle-down"></b-icon>
            </button>
          </template>
          <div v-for="etat in etats" :key="etat">
            <b-dropdown-item aria-role="listitem" :value="etat">
              {{ $t(etat) }}
            </b-dropdown-item>
          </div>
        </b-dropdown>
        <div v-else>
          <div class="navbar-item">
            <b-icon
              v-if="projetAnnuel.etat === 'ACTIF'"
              icon="play-circle"
              type="is-success"
            ></b-icon>
            <b-icon
              v-else-if="projetAnnuel.etat === 'EN_PAUSE'"
              icon="pause-circle"
              type="is-warning"
            ></b-icon>
            <b-icon v-else icon="stop-circle" type="is-danger"></b-icon>
            <span class="item-text bold">{{ $t(projetAnnuel.etat) }}</span>
          </div>
        </div>
      </SubHeaderItem>
    </div>
  </div>
  <ConfirmChangementEtat
    v-model="showConfirmModal"
    :old-etat-projet="projetAnnuel.etat"
    :new-etat-projet="selectedEtat"
    @confirm="confirmChangementEtat"
  />
</template>

<script lang="ts">
import { BDropdown, BDropdownItem, BIcon } from "buefy"

import { defineComponent, PropType } from "vue"
import type { EtatProjet } from "@/model/bean/GeneratedDTOs"
import {
  ParametrageDTO,
  PeriodDTO,
  ProjetAnnuelDTO,
  ProjetPatchDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { ProjectService } from "@/services/rest/project/ProjectService"
import SubHeaderItem from "@/views/commons/SubHeaderItem.vue"
import { ParametersService } from "@/services/rest/parameters/ParametersService"
import { UsersService } from "@/services/rest/users/UsersService"
import DateUtils from "@/services/DateUtils"
import ConfirmChangementEtat from "@/views/commons/ConfirmChangementEtat.vue"

const DATE_FORMAT = "d MMMM yyyy"
const SHORT_DATE_FORMAT = "MMM yyyy"

export default defineComponent({
  name: "ProjetYearAndStatutSelection",
  components: {
    BDropdownItem,
    SubHeaderItem,
    ConfirmChangementEtat,
    BDropdown,
    BIcon,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
    etats: {
      type: Array as PropType<EtatProjet[]>,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    isPorteurProjet: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["show-confirm-modal", "close-confirm-modal", "reload-project"],
  data() {
    return {
      // Services
      parametersService: ParametersService.INSTANCE,
      projetService: ProjectService.INSTANCE,
      usersService: UsersService.INSTANCE,

      // Component state
      selectedEtat: "ACTIF" as EtatProjet,
      parametrage: new ParametrageDTO(),
      periods: [] as PeriodDTO[],
      showConfirmModal: false,
      graeId: "",
      canChangeEtat: false,
      isObservateurNational: false,
    }
  },
  watch: {
    showConfirmModal(newValue: boolean): void {
      if (!newValue) {
        // Quand la modale se ferme, on réinitialise l'état sélectionné
        this.selectedEtat = this.projetAnnuel.etat
        this.$emit("close-confirm-modal")
      }
    },
    projetAnnuel: {
      handler(): void {
        this.selectedEtat = this.projetAnnuel.etat
        this.graeId = this.projetAnnuel.etablissements[0]?.grae?.id
      },
    },
  },
  created(): void {
    this.loadParameters()
    this.loadPeriods(this.projetAnnuel.projetId)
    this.projetAnnualChanged()
    this.isObservateurNational = this.usersService.isObservateurNational(this.loggedUser)
  },
  methods: {
    async loadParameters(): Promise<void> {
      await this.parametersService.getParametrage(this.projetAnnuel.grae.id).then((params) => {
        this.parametrage = params
      })
    },

    async loadPeriods(projectId: string): Promise<void> {
      this.periods = await this.projetService.getPeriods(projectId)
      this.canChangeEtat =
        (!this.isPorteurProjet || this.projetAnnuel.etat != "ARCHIVE") &&
        this.periods[0] &&
        this.periods[0].projetAnnuelId === this.projetAnnuel.id
    },

    projetAnnualChanged(): void {
      this.selectedEtat = this.projetAnnuel.etat
      this.graeId = this.projetAnnuel.etablissements[0]?.grae?.id
    },

    selectPeriod(index: number): void {
      // Reuse section and active tab if target dossier is of the same type
      const period = this.periods[index]
      let section = this.$router.currentRoute.value.params["section"]
      let activeTab = this.$router.currentRoute.value.params["activeTab"]
      const isPeriodInscription = period.typeDossier == "DOSSIER_INSCRIPTION"
      const isCurrentProjetAnnuelInscription =
        this.projetAnnuel.typeDossier == "DOSSIER_INSCRIPTION"
      if (isPeriodInscription != isCurrentProjetAnnuelInscription) {
        section = period.typeDossier == "DOSSIER_INSCRIPTION" ? "inscription" : "bref"
        activeTab = "0"
      }
      const path = [
        "/project",
        this.projetAnnuel.projetId,
        period.projetAnnuelId,
        section,
        activeTab,
      ].join("/")
      this.$router.push(path)
    },

    getPeriodLabel(debut: Date, fin: Date): string {
      return (
        this.formatDate(debut, SHORT_DATE_FORMAT) + " / " + this.formatDate(fin, SHORT_DATE_FORMAT)
      )
    },

    formatDate(date: Date, customFormat: string = DATE_FORMAT): string {
      return DateUtils.formatDateFromPattern(date, customFormat)
    },

    changeEtat(): void {
      this.showConfirmModal = true
      this.$emit("show-confirm-modal")
    },

    confirmChangementEtat(): void {
      const patch = { etat: this.selectedEtat } as ProjetPatchDTO
      this.projetService
        .patchProject(this.projetAnnuel.projetId, patch)
        .then(() => this.$emit("reload-project"))
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.choose-period > div:first-of-type {
  left: 0;
}

.inscription-title > div {
  left: 230px;
  background-color: $soft-blue;
  border-color: $ofb-bleu-1;
}

.only-in-inscription > div {
  left: 140px !important;
}

.change-etat > div:first-of-type {
  right: 400px;
}

@media screen and (max-width: 1050px) {
  .change-etat > div:first-of-type {
    right: 250px;
    width: 120px;

    a {
      display: flex;
      width: 120px !important;
    }
  }

  .date-depot {
    display: none;
  }
}

.subheader {
  position: relative;
  width: 100%;
  z-index: 2;
  display: flex;
  margin-bottom: 3px;
  background-color: #fbfbfb;
  box-shadow: #0000001a -1px 3px 10px 5px;
}

.end-sub-headers-container {
  display: flex;
  flex-wrap: nowrap;
  margin-left: auto;
}

#project-state,
.date-depot {
  position: relative;
  width: fit-content;

  .SubHeaderItem {
    position: initial;
    width: initial;
  }
}
</style>
